import { clearNavHistoryStorage, injectKeyFramesForAnimation, useNavigation, useNotification } from "o4a-react";
import React from "react";
import {
  Callout,
  IButtonStyles,
  Icon,
  IconButton,
  IPersonaSharedProps,
  mergeStyleSets,
  Overlay,
  Persona,
  PersonaPresence,
  PersonaSize,
  ProgressIndicator,
} from "@fluentui/react";
import { useBoolean, useId } from "@fluentui/react-hooks";
import * as Messages from "../codegen/Messages";
import { HOME_ROUTE, PageId } from "../constants/pluginConstants";
import { MenuStyleMode, topHeaderHeight } from "../constants/uiConstants";
import { useAppAuthContext } from "../hooks/useAppAuthContext";
import { logOut } from "../session/azureSession";
import { ConsoleContext } from "./ConsoleContext";
import { TopMenu } from "./TopMenu";

const classNames = mergeStyleSets({
  headerContainer: {
    backgroundColor: "#0078D4",
    color: "white",
    height: `${topHeaderHeight}px`,
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
  leftContainer: {
    display: "flex",
    minWidth: "220px",
  },
  rightContainer: { display: "flex" },
  starIcon: {
    fontSize: "16px",
    fontWeight: "bold",
  },
  hamburgerIconContainer: {
    display: "flex",
    paddingLeft: "15px",
    paddingRight: "15px",
    lineHeight: `${topHeaderHeight}px`,
    cursor: "pointer",
    ":hover": { backgroundColor: "#106ebe" },
  },
  brandName: {
    fontSize: "15px",
    padding: "0 20px",
    fontWeight: 600,
    lineHeight: `${topHeaderHeight}px`,
    cursor: "pointer",
    a: {
      textDecoration: "none",
      color: "#FFFFFF",
    },
    ":hover": { backgroundColor: "#106ebe" },
  },
  menuActionIcons: {
    display: "flex",
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  headerUserInfo: {
    display: "flex",
    width: "220px",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    alignItems: "center",
    fontSize: "12px",
    paddingLeft: "5px",
    paddingRight: "5px",
    textAlign: "right",
    cursor: "pointer",
    ":hover": { backgroundColor: "#106ebe" },
  },
  headerUserInfoCompany: { fontSize: "10px" },
  hamburgerOpenIcon: {
    height: `${topHeaderHeight}px`,
    backgroundColor: "#fff",
    width: "210px",
    display: "flex",
    alignItems: "center",
    paddingLeft: "15px",
    cursor: "pointer",
    ":hover": { backgroundColor: "#F0F7FE" },
  },
  hamburgerOpen: {
    fontSize: "16px",
    color: "#3078CD",
    fontWeight: "bold",
  },
  headerMenuOverlay: { msNav: { background: "white" } },
  notificationCount: {
    fontSize: "9px",
    textAlign: "center",
    borderRadius: "50%",
    border: "1px solid white",
    backgroundColor: "white",
    color: "#0078D4",
    width: "12px",
    height: "12px",
    display: "inline-block",
    lineHeight: "10px",
    position: "absolute",
    cursor: "pointer",
    top: "-4px",
    right: "10px",
  },
  profileCallout: {
    fontSize: "16px",
    marginRight: "-8px",
  },
  profileCallOutContainer: {
    paddingTop: "15px",
    paddingBottom: "30px",
  },
  signOutContainer: {
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  signOutLink: {
    display: "inline-block",
    textDecoration: "none",
    outline: "none",
    paddingBottom: "16px",
    paddingTop: "16px",
    fontSize: "13px",
    color: "#333",
    paddingLeft: "14px",
    paddingRight: "14px",
    ":hover": {
      backgroundColor: "rgba(0,0,0,.12)",
      textDecoration: "underline",
    },
  },
  signOutTenantName: {
    fontSize: "13px",
    color: "#333",
    paddingLeft: "14px",
    paddingRight: "14px",
  },
  showOverlay: { visibility: "visible" },
  hideOverlay: { visibility: "hidden" },
});

export enum ActionIds {
  CloudShell = "cloud-shell",
  Subscriptions = "subscriptions",
  Notifications = "notifications",
  Settings = "settings",
  Help = "help",
  Feedback = "feedback",
}
export interface Props {
  menuStyleMode?: MenuStyleMode,
  isOverlayOpen: boolean,
  openOverlay: () => void,
  dismissOverlay: () => void,
  onActionClick?: (actionId: ActionIds) => void;
  activeActionId?: ActionIds;
  noTopMenu?: boolean;
}

const getInitialsFromName = (name: string):
  string => ((name.match(/(\b\S)?/g) || []).join("").match(/(^\S|\S$)?/g) || []).join("").toUpperCase();

const getInitialsFromEmail = (email: string): string => {
  const emailRegex = /^(.*)\.(.*)@.*$/;
  const matches = email?.match(emailRegex);
  if (matches) {
    return `${matches[1].substring(0, 1).toUpperCase()}${matches[2].substring(0, 1).toUpperCase()}`;
  }
  return email?.substring(0, 1).toUpperCase();
};

const btnStyles: IButtonStyles = {
  root: {
    color: "white",
    width: "46px",
    height: "40px",
    borderRadius: 0,
  },
  rootHovered: {
    backgroundColor: "#106ebe",
    color: "white",
  },
  rootPressed: {
    backgroundColor: "#135995",
    color: "white",
  },
  rootChecked: {
    backgroundColor: "white",
    color: "#0078D4",
  },
  rootCheckedHovered: {
    backgroundColor: "white",
    color: "#0078D4",
  },
  rootDisabled: { backgroundColor: "rgb(0, 120, 212)" },
};

export const TopHeader = ({
  menuStyleMode,
  isOverlayOpen,
  openOverlay,
  dismissOverlay,
  onActionClick,
  activeActionId,
  noTopMenu,
}: Props): JSX.Element => {
  injectKeyFramesForAnimation(`@keyframes IndeterminateProgress {
    from {
      left: -100%
    }
    to {
      left: 150%
    }
  }`);

  const [isProfileCalloutVisible, { toggle: toggleIsProfileCalloutVisible }] = useBoolean(false);
  const labelId = useId("callout-label");
  const descriptionId = useId("callout-description");
  const buttonId = useId("callout-button");

  const { domainName, name, tenantName, tenant, username } = useAppAuthContext();
  const { navigateTo } = useNavigation(ConsoleContext);
  const { unreadCount, inProgressCount } = useNotification();

  const currentPersona: IPersonaSharedProps = {
    imageUrl: "https://www.oracle.com",
    imageInitials: name ? getInitialsFromName(name) : getInitialsFromEmail(username),
    text: name,
    secondaryText: username,
    tertiaryText: tenantName,
  };

  const navigateToHome = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    navigateTo(HOME_ROUTE, PageId.HOME, undefined, true);
  };

  const getPersonaToolip = (): string => {
    const personaName = Messages.labels.personaName(name || "");
    const personaEmail = Messages.labels.personaEmail(username || "");
    const personaDirectory = Messages.labels.personaDirectory(tenantName || "", tenant || "");
    const personaDomain = Messages.labels.personaDomain(domainName || "");
    return `${personaName}\r\n${personaEmail}\r\n${personaDirectory}\r\n${personaDomain}`;
  };

  return (
    <header className={classNames.headerContainer}>
      <div className={classNames.leftContainer}>
        {(MenuStyleMode.FlyOut === menuStyleMode) && !noTopMenu
          && (
            <div
              onClick={openOverlay}
              onKeyPress={() => ({})}
              className={classNames.hamburgerIconContainer}
              role="button"
              tabIndex={0}
            >
              <Icon
                iconName="GlobalNavButton"
                aria-label={Messages.topHeader.showPortalMenu()}
                title={Messages.topHeader.showPortalMenu()}
                className={classNames.starIcon}
              />
            </div>
          )}
        <div className={classNames.brandName}>
          <a rel="noreferrer" onClick={navigateToHome} href="" title="Home">
            {Messages.portalBrand.long()}
          </a>
        </div>
      </div>
      <div className={classNames.rightContainer}>
        <ul className={classNames.menuActionIcons}>
          {false && (
            <li>
              <IconButton
                iconProps={{ iconName: "CommandPrompt" }}
                styles={btnStyles}
                title={Messages.topHeader.cloudShell()}
                ariaLabel={Messages.topHeader.cloudShell()}
                onClick={() => onActionClick?.(ActionIds.CloudShell)}
                checked={activeActionId === ActionIds.CloudShell}
              />
            </li>
          )}
          {false && (
            <li>
              <IconButton
                iconProps={{ iconName: "PageListFilter" }}
                styles={btnStyles}
                title={Messages.topHeader.directoriesSubscriptions()}
                ariaLabel={Messages.topHeader.directoriesSubscriptions()}
                onClick={() => onActionClick?.(ActionIds.Subscriptions)}
                checked={activeActionId === ActionIds.Subscriptions}
              />
            </li>
          )}
          <li>
            <span style={{ position: "relative" }}>
              <IconButton
                iconProps={{ iconName: "Ringer" }}
                styles={btnStyles}
                title={Messages.topHeader.notifications()}
                ariaLabel={Messages.topHeader.notifications()}
                onClick={() => onActionClick?.(ActionIds.Notifications)}
                checked={activeActionId === ActionIds.Notifications}
              />
              {unreadCount > 0 && (
                <div
                  className={classNames.notificationCount}
                  onClick={() => onActionClick?.(ActionIds.Notifications)}
                  role="button"
                  onKeyPress={() => ({})}
                  tabIndex={0}
                >
                  {unreadCount}
                </div>
              )}
              {inProgressCount > 0 && (
                <ProgressIndicator
                  styles={{
                    root: { marginTop: "-4px" },
                    itemProgress: { padding: "0px" },
                    progressBar: {
                      minWidth: "60%",
                      background: "#00bcf2",
                      animation: "IndeterminateProgress 1.5s linear infinite",
                    },
                    progressTrack: { backgroundColor: "inherit" },
                  }}
                />
              )}
            </span>
          </li>
          <li>
            <IconButton
              iconProps={{ iconName: "Settings" }}
              styles={btnStyles}
              title={Messages.topHeader.settings()}
              ariaLabel={Messages.topHeader.settings()}
              onClick={() => onActionClick?.(ActionIds.Settings)}
              checked={activeActionId === ActionIds.Settings}
            />
          </li>
          <li>
            <IconButton
              iconProps={{ iconName: "help-svg" }}
              styles={btnStyles}
              title={Messages.topHeader.help()}
              ariaLabel={Messages.topHeader.help()}
              onClick={() => onActionClick?.(ActionIds.Help)}
              checked={activeActionId === ActionIds.Help}
            />
          </li>
          <li>
            <IconButton
              iconProps={{ iconName: "Feedback" }}
              styles={btnStyles}
              title={Messages.topHeader.feedback()}
              ariaLabel={Messages.topHeader.feedback()}
              onClick={() => onActionClick?.(ActionIds.Feedback)}
              checked={activeActionId === ActionIds.Feedback}
            />
          </li>
        </ul>
        <div className={classNames.headerUserInfo}>
          <Persona
            imageUrl={currentPersona.imageUrl}
            imageInitials={currentPersona.imageInitials}
            text={currentPersona.secondaryText}
            secondaryText={currentPersona.tertiaryText?.toUpperCase()}
            size={PersonaSize.size24}
            coinSize={28}
            presence={PersonaPresence.online}
            hidePersonaDetails={false}
            onClick={toggleIsProfileCalloutVisible}
            showSecondaryText
            id={buttonId}
            title={getPersonaToolip()}
            styles={{
              root: {
                display: "flex",
                flexDirection: "row-reverse",
              },
              primaryText: {
                textAlign: "right",
                color: "white",
                fontSize: "14px",
                lineHeight: "1",
                paddingTop: "3px",
                ":hover": { color: "white" },
              },
              secondaryText: {
                textAlign: "right",
                color: "white",
                fontSize: "10px",
                fontWeight: "600",
                marginTop: "-2px",
              },
            }}
          />
          {isProfileCalloutVisible && (
            <Callout
              className={classNames.profileCallout}
              ariaLabelledBy={labelId}
              ariaDescribedBy={descriptionId}
              gapSpace={0}
              target={`#${buttonId}`}
              onDismiss={toggleIsProfileCalloutVisible}
              setInitialFocus
              beakWidth={0}
              styles={{
                root: {
                  width: "320px",
                  zIndex: 1,
                  backgroundColor: "#FFFFFF",
                  marginTop: "6px",
                },
              }}
            >
              <div className={classNames.signOutContainer}>
                <div className={classNames.signOutTenantName}>{tenantName}</div>
                <a
                  className={classNames.signOutLink}
                  href="#"
                  title={Messages.topHeader.signOut()}
                  onClick={() => {
                    clearNavHistoryStorage();
                    logOut();
                  }}
                >
                  {Messages.topHeader.signOut()}
                </a>
              </div>
              <div className={classNames.profileCallOutContainer}>
                <Persona
                  imageUrl={currentPersona.imageUrl}
                  imageInitials={currentPersona.imageInitials}
                  text={currentPersona.text}
                  secondaryText={currentPersona.secondaryText}
                  presence={PersonaPresence.online}
                  hidePersonaDetails={false}
                  onClick={toggleIsProfileCalloutVisible}
                  id={buttonId}
                  size={PersonaSize.size72}
                  coinSize={88}
                  styles={{
                    root: {
                      paddingLeft: "15px",
                      paddingRight: "15px",
                    },
                    primaryText: {
                      fontSize: "18px",
                      color: "#333",
                      fontWeight: "700",
                    },
                    secondaryText: {
                      fontSize: "13px",
                      color: "#333",
                      marginTop: "3px",
                    },
                    tertiaryText: {
                      fontSize: "13px",
                      color: "#333",
                      marginTop: "3px",
                    },
                  }}
                />
              </div>
            </Callout>
          )}
        </div>
        {!noTopMenu && (
          <div className={isOverlayOpen ? classNames.showOverlay : classNames.hideOverlay}>
            <Overlay
              styles={{ root: { backgroundColor: "rgba(255,255,255,0)", zIndex: 1 } }}
              className={classNames.headerMenuOverlay}
              onClick={dismissOverlay}
            >
              <>
                <div
                  role="presentation"
                  className={classNames.hamburgerOpenIcon}
                  onClick={() => {
                    dismissOverlay();
                  }}
                >
                  <Icon
                    iconName="GlobalNavButton"
                    className={classNames.hamburgerOpen}
                    aria-label={Messages.topHeader.hidePortalMenu()}
                    title={Messages.topHeader.hidePortalMenu()}
                  />
                </div>
                <TopMenu
                  menuStyleMode={MenuStyleMode.FlyOut}
                />
              </>
            </Overlay>
          </div>
        )}
      </div>
    </header>
  );
};
